import React, { useState, useEffect } from 'react';
import { 
  ArrowRightLeft,
  BarChart2,
  Plus,
  Filter,
  Eye,
  EyeOff,
  ChevronRight,
  ListFilter,
  BarChart3,
  Pencil,
  Trash2,
  RefreshCw,
  FileX
} from 'lucide-react';
import { financeService, FinancialTransaction } from '../services/database/supabase';
import { TransactionFilters } from '../components/finances/TransactionFilters';
import { TransactionForm } from '../components/finances/TransactionForm';
import { FinanceDashboard } from '../components/finances/FinanceDashboard';
import { toast } from 'sonner';

// Componente de Card
const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-8 border border-white/[0.08] shadow-2xl ${className}`}>
    {children}
  </div>
);

// Componente de Botão
const Button = ({ 
  children, 
  onClick, 
  variant = 'primary',
  className = '' 
}: { 
  children: React.ReactNode; 
  onClick?: () => void; 
  variant?: 'primary' | 'secondary';
  className?: string;
}) => {
  const baseClasses = "h-[48px] px-6 rounded-xl font-everett text-[14px] transition-all flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-white text-black hover:bg-[#F2F2F2]",
    secondary: "bg-white/[0.08] text-white hover:bg-white/[0.12]"
  };
  
  return (
    <button 
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

const Finances = () => {
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<FinancialTransaction[]>([]);
  const [filters, setFilters] = useState({
    type: '',
    category: '',
    owner: '',
    status: '',
    startDate: '',
    endDate: '',
  });
  const [balance, setBalance] = useState(0);
  const [isAddingTransaction, setIsAddingTransaction] = useState(false);
  const [isEditingTransaction, setIsEditingTransaction] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<FinancialTransaction | null>(null);
  const [newTransaction, setNewTransaction] = useState<Partial<FinancialTransaction>>({
    type: 'Despesas',
    owner: 'Luiz',
    status: 'Not started',
    date: new Date().toISOString().split('T')[0]
  });
  const [monthlyReport, setMonthlyReport] = useState<any>({
    byCategory: {
      'Alimentação': 2800,
      'Transporte': 1500,
      'Lazer': 1200,
      'Moradia': 3000,
      'Educação': 800,
      'Saúde': 600,
    },
    monthlyGoals: [
      { name: 'Economia', current: 2000, target: 3000, color: '#1AD1A5' },
      { name: 'Investimentos', current: 1500, target: 2000, color: '#4C6EF5' },
      { name: 'Fundo de Emergência', current: 5000, target: 10000, color: '#FF922B' },
    ]
  });
  const [chartData, setChartData] = useState<any>({
    labels: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho'],
    datasets: [
      {
        label: 'Receitas',
        data: [1200, 1900, 3000, 5000, 2000, 3000],
        borderColor: '#1AD1A5',
        backgroundColor: 'rgba(26, 209, 165, 0.1)',
        borderWidth: 2,
        pointBackgroundColor: '#1AD1A5',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Despesas',
        data: [1000, 2000, 2500, 2800, 1500, 2000],
        borderColor: '#FF6B6B',
        backgroundColor: 'rgba(255, 107, 107, 0.1)',
        borderWidth: 2,
        pointBackgroundColor: '#FF6B6B',
        tension: 0.4,
        fill: true,
      },
    ],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showRefreshTooltip, setShowRefreshTooltip] = useState(false);
  const [isBlurred, setIsBlurred] = useState(true);
  const [password, setPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [view, setView] = useState<'analytics' | 'default'>('analytics');

  useEffect(() => {
    loadTransactions();
    loadBalance();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, transactions]);

  const loadTransactions = async () => {
    setIsLoading(true);
    try {
      const data = await financeService.getTransactions();
      setTransactions(data);
      setFilteredTransactions(data);
    } catch (error) {
      console.error('Erro ao carregar transações:', error);
      toast.error('Erro ao carregar transações');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBalance = async () => {
    try {
      const balance = await financeService.getBalance();
      setBalance(balance);
    } catch (error) {
      console.error('Erro ao carregar saldo:', error);
      toast.error('Erro ao carregar saldo');
    }
  };

  const handleEditTransaction = (transaction: FinancialTransaction) => {
    setSelectedTransaction(transaction);
    setNewTransaction({
      description: transaction.description,
      amount: transaction.amount,
      category: transaction.category,
      type: transaction.type,
      owner: transaction.owner,
      status: transaction.status,
      date: transaction.date
    });
    setIsEditingTransaction(true);
    setIsAddingTransaction(true);
  };

  const handleDeleteTransaction = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta transação?')) {
      try {
        await financeService.deleteTransaction(id);
        setTransactions(transactions.filter(t => t.id !== id));
        toast.success('Transação excluída com sucesso!');
        loadBalance();
      } catch (error) {
        console.error('Erro ao excluir transação:', error);
        toast.error('Erro ao excluir transação');
      }
    }
  };

  const handleSaveTransaction = async () => {
    try {
      if (!newTransaction.description || !newTransaction.amount || !newTransaction.category || !newTransaction.date) {
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      if (isEditingTransaction && selectedTransaction) {
        const updated = await financeService.updateTransaction(selectedTransaction.id, newTransaction);
        setTransactions(transactions.map(t => 
          t.id === selectedTransaction.id ? updated : t
        ));
        toast.success('Transação atualizada com sucesso!');
      } else {
        const added = await financeService.addTransaction(newTransaction as Omit<FinancialTransaction, 'id' | 'created_at'>);
        setTransactions([added, ...transactions]);
        toast.success('Transação adicionada com sucesso!');
      }

      setIsAddingTransaction(false);
      setIsEditingTransaction(false);
      setSelectedTransaction(null);
      setNewTransaction({
        type: 'Despesas',
        owner: 'Luiz',
        status: 'Not started',
        date: new Date().toISOString().split('T')[0]
      });
      loadBalance();
    } catch (error) {
      console.error('Erro ao salvar transação:', error);
      toast.error('Erro ao salvar transação');
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    if (filters.type) {
      filtered = filtered.filter(t => t.type === filters.type);
    }
    if (filters.category) {
      filtered = filtered.filter(t => t.category === filters.category);
    }
    if (filters.owner) {
      filtered = filtered.filter(t => t.owner === filters.owner);
    }
    if (filters.status) {
      filtered = filtered.filter(t => t.status === filters.status);
    }
    if (filters.startDate) {
      filtered = filtered.filter(t => new Date(t.date) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      filtered = filtered.filter(t => new Date(t.date) <= new Date(filters.endDate));
    }

    setFilteredTransactions(filtered);
  };

  const generateChartData = (transactions: FinancialTransaction[]) => {
    const dates = transactions.map(t => new Date(t.date).toLocaleDateString());
    const uniqueDates = [...new Set(dates)];

    const incomeData = uniqueDates.map(date => 
      transactions
        .filter(t => new Date(t.date).toLocaleDateString() === date && t.type === 'Receita')
        .reduce((sum, t) => sum + t.amount, 0)
    );

    const expenseData = uniqueDates.map(date => 
      transactions
        .filter(t => new Date(t.date).toLocaleDateString() === date && t.type === 'Despesas')
        .reduce((sum, t) => sum + t.amount, 0)
    );

    return {
      labels: uniqueDates,
      datasets: [
        {
          label: 'Receitas',
          data: incomeData,
          borderColor: '#1AD1A5',
          backgroundColor: 'rgba(26, 209, 165, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#1AD1A5',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Despesas',
          data: expenseData,
          borderColor: '#FF6B6B',
          backgroundColor: 'rgba(255, 107, 107, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#FF6B6B',
          tension: 0.4,
          fill: true,
        }
      ]
    };
  };

  const handleToggleBlur = () => {
    setIsBlurred(!isBlurred);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'mais deus') {
      setIsBlurred(false);
      setShowPasswordModal(false);
      setPassword('');
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
      e.preventDefault();
      loadTransactions();
      toast.success('Dados atualizados!');
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Finanças</h1>
              <p className="text-gray-400 mt-2">
                Gerencie suas transações e acompanhe seus gastos
              </p>
            </div>
            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => setView('analytics')}
                className={view === 'analytics' ? 'bg-white/[0.12]' : ''}
              >
                <BarChart2 size={20} />
                Análise
              </Button>
              <Button
                variant="secondary"
                onClick={() => setView('default')}
                className={view === 'default' ? 'bg-white/[0.12]' : ''}
              >
                <ListFilter size={20} />
                Dash
              </Button>
              <Button
                variant="secondary"
                onClick={handleToggleBlur}
                className="w-12"
              >
                {isBlurred ? <Eye size={20} /> : <EyeOff size={20} />}
              </Button>
            </div>
          </div>
        </div>

        {view === 'analytics' ? (
          <FinanceDashboard
            transactions={transactions}
            balance={balance}
            isBlurred={isBlurred}
            onToggleBlur={handleToggleBlur}
            monthlyGoals={[
              { name: 'Economia', current: 2000, target: 3000, color: '#1AD1A5' },
              { name: 'Investimentos', current: 1500, target: 2000, color: '#4C6EF5' },
              { name: 'Fundo de Emergência', current: 5000, target: 10000, color: '#FF922B' },
            ]}
          />
        ) : (
          <>
            {/* Filtros */}
            <TransactionFilters
              filters={filters}
              onFilterChange={setFilters}
            />

            {/* Lista de Transações */}
            <div className="space-y-4 mt-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Transações</h2>
                <Button onClick={() => setIsAddingTransaction(true)}>
                  <Plus size={20} />
                  Nova Transação
                </Button>
              </div>

              {filteredTransactions.length === 0 ? (
                <div className="bg-[#111]/80 backdrop-blur-xl rounded-[20px] p-8 text-center border border-white/[0.08]">
                  <FileX size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-white/60">Nenhuma transação encontrada</p>
                </div>
              ) : (
                filteredTransactions.map((transaction) => (
                  <div key={transaction.id} className="bg-[#111]/50 p-4 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        transaction.type === 'Receita' || transaction.type === 'Salário' 
                          ? 'bg-emerald-500/20 text-emerald-500' 
                          : 'bg-red-500/20 text-red-500'
                      }`}>
                        <ArrowRightLeft size={20} />
                      </div>
                      <div>
                        <h3 className="font-medium">{transaction.description}</h3>
                        <div className="flex items-center gap-3 text-sm text-white/60">
                          <span>{transaction.category}</span>
                          <span>•</span>
                          <span>{transaction.owner}</span>
                          <span>•</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            transaction.status === 'Done' 
                              ? 'bg-emerald-500/20 text-emerald-500' 
                              : transaction.status === 'In progress'
                                ? 'bg-blue-500/20 text-blue-500'
                                : 'bg-orange-500/20 text-orange-500'
                          }`}>
                            {transaction.status === 'Done' 
                              ? 'Concluído' 
                              : transaction.status === 'In progress'
                                ? 'Em andamento'
                                : 'Não iniciado'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <span className={`block font-medium ${
                          transaction.type === 'Receita' || transaction.type === 'Salário'
                            ? 'text-emerald-500'
                            : 'text-red-500'
                        }`}>
                          {isBlurred ? '****' : `R$ ${transaction.amount.toFixed(2)}`}
                        </span>
                        <span className="block text-sm text-white/60">
                          {new Date(transaction.date).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditTransaction(transaction)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteTransaction(transaction.id)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors text-red-500"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* Modal de Transação */}
        {isAddingTransaction && (
          <TransactionForm
            transaction={newTransaction}
            onTransactionChange={setNewTransaction}
            onSave={handleSaveTransaction}
            onCancel={() => {
              setIsAddingTransaction(false);
              setIsEditingTransaction(false);
              setSelectedTransaction(null);
              setNewTransaction({
                type: 'Despesas',
                owner: 'Luiz',
                status: 'Not started',
                date: new Date().toISOString().split('T')[0]
              });
            }}
            isEditing={isEditingTransaction}
          />
        )}
      </div>
    </div>
  );
};

export default Finances;
